import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { isAuthenticated } from "../../network/service/UserService";
import {
  getIntelligenceSummary,
  getScoringRules,
  rescoreCatalog,
} from "../../network/service/NutritionService";
import {
  canViewNutritionReviews,
  hasPermission,
  platformUser,
  PRODUCT_UPDATE,
} from "../../network/service/PlatformAuth";
import { SOURCE_NAMES } from "../products/nutrition/nutrition-model";
import PlatformSignInNotice from "../products/nutrition/platform-sign-in-notice";
import { GRADE_NAMES } from "../products/nutrition/grade-names";
import "../products/nutrition/nutrition-panel.css";

const GRADE_LABELS = { ...GRADE_NAMES, UNRATED: "Not rated" };
const STATUS_LABELS = {
  NO_DATA: "No data",
  SUSPECT: "Values look wrong",
  CONFLICT: "Sources disagree",
  UNRATED: "Missing facts",
  REVIEW: "Auto-filled, to check",
  RATED: "Graded",
};

const pct = (n, total) => (total ? `${Math.round((n * 100) / total)}%` : "–");

function Stat({ label, value, total, tone }) {
  return (
    <div className={`nutri-stat${tone ? ` nutri-stat-${tone}` : ""}`}>
      <div className="nutri-stat-value">{value}</div>
      <div>{label}</div>
      {total !== undefined && <small className="nutri-muted">{pct(value, total)}</small>}
    </div>
  );
}

function Counts({ title, counts, labels = {}, total }) {
  return (
    <div className="nutri-card">
      <h6>{title}</h6>
      <table className="table table-sm mb-0">
        <tbody>
          {Object.entries(counts).map(([k, v]) => (
            <tr key={k}>
              <td>{labels[k] ?? k}</td>
              <td className="text-end">{v}</td>
              {total !== undefined && <td className="text-end nutri-muted">{pct(v, total)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Overview({ canEdit }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [rescore, setRescore] = useState({ busy: false, result: null });

  const load = useCallback(async () => {
    setError("");
    try {
      setData(await getIntelligenceSummary());
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onRescore = async () => {
    setRescore({ busy: true, result: null });
    try {
      const result = await rescoreCatalog();
      setRescore({ busy: false, result });
      load();
    } catch (e) {
      setRescore({ busy: false, result: null });
      setError(e.message);
    }
  };

  if (error) return <div className="nutri-status nutri-status-error">{error}</div>;
  if (!data) return <p className="nutri-muted">Loading…</p>;

  const n = data.products.withNutrition;
  const r = rescore.result;
  return (
    <>
      <div className="nutri-stats">
        <Stat label="Active products" value={data.products.total} />
        <Stat label="With nutrition" value={n} total={data.products.total} />
        <Stat label="Graded" value={data.products.rated} total={n} tone="ok" />
        <Stat label="High confidence" value={data.confidence.HIGH} total={n} tone="ok" />
        <Stat label="Medium confidence" value={data.confidence.MEDIUM} total={n} tone="warn" />
        <Stat label="Low confidence" value={data.confidence.LOW} total={n} tone="error" />
      </div>

      <div className={`nutri-status ${data.history.outdated ? "nutri-status-warn" : "nutri-status-ok"}`}>
        Algorithm <strong>{data.scoreVersion}</strong>:{" "}
        {data.history.outdated
          ? `${data.history.outdated} products have no score recorded under this version yet.`
          : "every product's latest recorded score uses this version."}
        {canEdit && data.history.outdated > 0 && (
          <button type="button" className="btn btn-sm btn-purple-bg ms-3" disabled={rescore.busy} onClick={onRescore}>
            {rescore.busy ? "Re-scoring…" : "Record scores under this version"}
          </button>
        )}
      </div>

      {r && (
        <div className="nutri-card">
          <h6>Re-scored {r.rescored} products with {r.scoreVersion}</h6>
          <p className="nutri-muted">
            {r.scoreChanged} scores changed, {r.gradeChanged} grades changed, {r.firstScored} scored for the first time.
            Each product's score history explains its change.
          </p>
          {r.regraded.length > 0 && (
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Before</th>
                  <th>After</th>
                </tr>
              </thead>
              <tbody>
                {r.regraded.map((c) => (
                  <tr key={c.productId}>
                    <td>{c.name ?? `#${c.productId}`}</td>
                    <td>
                      {c.before.score} · {GRADE_LABELS[c.before.grade] ?? c.before.grade}
                    </td>
                    <td>
                      {c.after.score} · {GRADE_LABELS[c.after.grade] ?? c.after.grade}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="nutri-grid-cards">
        <Counts title="Worklist" counts={data.status} labels={STATUS_LABELS} total={data.products.total} />
        <Counts title="Grades" counts={data.grades} labels={GRADE_LABELS} total={n} />
        <Counts
          title={`Data completeness (average ${data.completeness.average}%)`}
          counts={{
            "80% or more": data.completeness.complete,
            "50–79%": data.completeness.partial,
            "Under 50%": data.completeness.sparse,
          }}
          total={n}
        />
        <Counts title="Main data source" counts={data.sources} labels={SOURCE_NAMES} total={n} />
        <Counts
          title="Verification"
          counts={{
            "Checked against the pack": data.verification.verified,
            "Approved by an expert": data.verification.expertApproved,
            "Rejected by an expert": data.verification.expertRejected,
            "Auto-filled, to check": data.verification.autoFilledToCheck,
            "Values from several sources": data.multiSource,
          }}
          total={n}
        />
        <Counts
          title="Food category"
          counts={{
            "Set by an admin or source": data.categories.declared,
            "Guessed from the facts": data.categories.inferred,
            Unknown: data.categories.unknown,
          }}
          total={n}
        />
      </div>
    </>
  );
}

function Rules() {
  const [rules, setRules] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    getScoringRules()
      .then(setRules)
      .catch((e) => setError(e.message));
  }, []);

  if (error) return <div className="nutri-status nutri-status-error">{error}</div>;
  if (!rules) return <p className="nutri-muted">Loading…</p>;

  const refName = (code) => rules.references.find((r) => r.code === code)?.name ?? "";
  return (
    <>
      <div className="nutri-status nutri-status-info">
        <strong>{rules.scoreVersion}</strong> · {rules.status}. Rules are changed in code with tests and a new version
        number, so every recorded score can be explained.
      </div>

      <div className="nutri-card">
        <h6>Score components (starting points {rules.weights.base})</h6>
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Component</th>
              <th className="text-end">Points</th>
              <th>Rule</th>
              <th>Reference</th>
            </tr>
          </thead>
          <tbody>
            {rules.components.map((c) => (
              <tr key={c.code}>
                <td>{c.code.toLowerCase().replace(/_/g, " ")}</td>
                <td className={`text-end ${c.max < 0 ? "text-danger" : "text-success"}`}>
                  {c.max > 0 ? `up to +${c.max}` : `up to ${c.max}`}
                </td>
                <td>{c.rule}</td>
                <td className="nutri-muted">{c.reference && refName(c.reference)}</td>
              </tr>
            ))}
            {rules.limits.map((l) => (
              <tr key={l.code}>
                <td>{l.code.toLowerCase().replace(/_/g, " ")}</td>
                <td className="text-end">{l.maxScore !== undefined ? `max ${l.maxScore}` : `min ${l.minScore}`}</td>
                <td>{l.rule}</td>
                <td className="nutri-muted">{l.reference && refName(l.reference)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="nutri-grid-cards">
        <div className="nutri-card">
          <h6>Grades</h6>
          <table className="table table-sm mb-0">
            <tbody>
              {rules.grades.map((g) => (
                <tr key={g.grade}>
                  <td>{g.label}</td>
                  <td className="text-end">{g.min}+</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="nutri-card">
          <h6>Low / high thresholds per 100 g (per 100 ml)</h6>
          <table className="table table-sm mb-0">
            <tbody>
              {Object.keys(rules.trafficLights.food).map((k) => (
                <tr key={k}>
                  <td>{k}</td>
                  <td className="text-end">
                    {rules.trafficLights.food[k].join(" / ")} g ({rules.trafficLights.beverage[k].join(" / ")})
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="nutri-card">
          <h6>Data source priority</h6>
          <ol className="mb-1">
            {rules.dataSources.priority.map((p) => {
              const [source, kind] = p.split(":");
              return (
                <li key={p}>
                  {SOURCE_NAMES[source] ?? source}
                  {kind === "BARCODE" ? " (barcode match)" : source === "OPEN_FOOD_FACTS" ? " (name match)" : ""}
                </li>
              );
            })}
          </ol>
          <small className="nutri-muted">
            Sources conflict when they differ by more than {Math.round(rules.dataSources.conflictRatio * 100)}%.
          </small>
        </div>
      </div>

      <div className="nutri-card">
        <h6>Food categories</h6>
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Category</th>
              <th className="text-end">Food-group points</th>
              <th>Saturated fat judged by</th>
              <th>Limits</th>
            </tr>
          </thead>
          <tbody>
            {rules.categories.map((c) => (
              <tr key={c.code}>
                <td>{c.name}</td>
                <td className="text-end">{c.foodGroup || "–"}</td>
                <td>
                  {c.saturatedFat === "RATIO" ? "share of total fat" : "g per 100 g"}
                  {c.saturatedFatHigh ? ` (full penalty at ${c.saturatedFatHigh} g)` : ""}
                </td>
                <td>
                  {[c.floor && `min ${c.floor} when unprocessed`, c.cap && `max ${c.cap.maxScore}: ${c.cap.reason}`]
                    .filter(Boolean)
                    .join("; ")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="nutri-card">
        <h6>Micronutrient reference (ICMR-NIN 2020, adult)</h6>
        <div className="nutri-chips">
          {rules.micronutrients.map((m) => (
            <span key={m.code} className="nutri-chip nutri-chip-plain">
              {m.name}: {m.rda} {m.unit}
            </span>
          ))}
        </div>
      </div>

      <div className="nutri-card">
        <h6>References</h6>
        <ul className="mb-0">
          {rules.references.map((r) => (
            <li key={r.code}>
              <strong>{r.name}</strong> — {r.usedFor}
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

/** Catalog-wide nutrition data health and the scoring rules (read-only). */
function NutritionIntelligence() {
  const user = platformUser();
  const [tab, setTab] = useState("overview");
  const canView = !!user && canViewNutritionReviews();
  const canEdit = !!user && hasPermission(PRODUCT_UPDATE);

  if (!isAuthenticated() && !user) {
    return <Navigate to="/nutritionist-login" replace />;
  }

  return (
    <main className="container-fluid dashboard">
      <div className="nutri-panel">
        <div className="nutri-header">
          <div>
            <h4>Nutrition intelligence</h4>
            <p className="nutri-muted mb-0">
              How complete and trustworthy the catalog's nutrition data is, and the rules behind every EatProtein
              Score.
            </p>
          </div>
        </div>

        {!user && <PlatformSignInNotice />}
        {user && !canView && (
          <div className="nutri-status nutri-status-warn">Your admin role cannot view nutrition data.</div>
        )}

        {canView && (
          <>
            <div className="nutri-tabs">
              {[
                ["overview", "Overview"],
                ["rules", "Score rules"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`nutri-tab${tab === value ? " active" : ""}`}
                  onClick={() => setTab(value)}
                >
                  {label}
                </button>
              ))}
            </div>
            {tab === "overview" ? <Overview canEdit={canEdit} /> : <Rules />}
          </>
        )}
      </div>
    </main>
  );
}

export default NutritionIntelligence;
